import React, { useState } from "react";
import { dashboardStats } from "../data/mock";
import { BarChart3, TrendingUp, Users, Package, Smartphone, Monitor, Tablet } from "lucide-react";

const Dashboard = () => {
  const [activeView, setActiveView] = useState('desktop');

  const viewOptions = [
    { id: 'desktop', label: 'Escritorio', icon: Monitor },
    { id: 'tablet', label: 'Tablet', icon: Tablet },
    { id: 'mobile', label: 'Móvil', icon: Smartphone }
  ];

  return (
    <section id="dashboard" className="py-20 bg-gray-900">
      <div className="container mx-auto px-6">
        <div className="text-center mb-16">
          <div className="inline-block bg-blue-100 text-blue-800 px-4 py-2 rounded-full text-sm font-medium mb-4">
            Dashboard Inteligente
          </div>
          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
            Visualiza tu negocio en <span className="text-blue-400">tiempo real</span>
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Dashboard ejecutivo con métricas en vivo, análisis predictivo y alertas inteligentes 
            que te ayudan a tomar decisiones informadas al instante.
          </p>
        </div>

        {/* Device Selector */}
        <div className="flex justify-center mb-12">
          <div className="bg-gray-800 p-2 rounded-xl">
            {viewOptions.map(option => {
              const IconComponent = option.icon;
              return (
                <button
                  key={option.id}
                  onClick={() => setActiveView(option.id)}
                  className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-medium transition-all ${
                    activeView === option.id 
                      ? 'bg-blue-600 text-white' 
                      : 'text-gray-400 hover:text-white hover:bg-gray-700'
                  }`}
                >
                  <IconComponent size={18} />
                  <span>{option.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Dashboard Preview */}
        <div className="relative max-w-6xl mx-auto">
          <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-3xl p-8 shadow-2xl border border-gray-700">
            {/* Header */}
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center space-x-4">
                <img 
                  src="https://customer-assets.emergentagent.com/job_inventory-manager-30/artifacts/sis1j2zy_logo.png" 
                  alt="RESET Logo" 
                  className="h-8 w-auto"
                />
                <div>
                  <h3 className="text-xl font-bold text-white">Dashboard SAIP</h3>
                  <p className="text-gray-400 text-sm">Panel de Control Ejecutivo</p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                <span className="text-gray-300 text-sm">En vivo</span>
              </div>
            </div>

            {/* Stats Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-6 rounded-2xl">
                <div className="flex items-center justify-between mb-4">
                  <BarChart3 className="text-white" size={24} />
                  <span className="text-blue-200 text-sm">{dashboardStats.sales.growth}</span>
                </div>
                <div className="text-3xl font-bold text-white mb-1">
                  ${dashboardStats.sales.monthly.toLocaleString()}
                </div>
                <div className="text-blue-200 text-sm">Ventas del mes</div>
              </div>

              <div className="bg-gradient-to-br from-green-500 to-green-600 p-6 rounded-2xl">
                <div className="flex items-center justify-between mb-4">
                  <Package className="text-white" size={24} />
                  <span className="text-green-200 text-sm">+{dashboardStats.products.newThisMonth}</span>
                </div>
                <div className="text-3xl font-bold text-white mb-1">
                  {dashboardStats.products.total.toLocaleString()}
                </div>
                <div className="text-green-200 text-sm">Productos totales</div>
              </div>

              <div className="bg-gradient-to-br from-purple-500 to-purple-600 p-6 rounded-2xl">
                <div className="flex items-center justify-between mb-4">
                  <Users className="text-white" size={24} />
                  <span className="text-purple-200 text-sm">{dashboardStats.customers.satisfaction}★</span>
                </div>
                <div className="text-3xl font-bold text-white mb-1">
                  {dashboardStats.customers.total.toLocaleString()}
                </div>
                <div className="text-purple-200 text-sm">Clientes activos</div>
              </div>

              <div className="bg-gradient-to-br from-orange-500 to-orange-600 p-6 rounded-2xl">
                <div className="flex items-center justify-between mb-4">
                  <TrendingUp className="text-white" size={24} />
                  <span className="text-orange-200 text-sm">{dashboardStats.performance.efficiency}</span>
                </div>
                <div className="text-3xl font-bold text-white mb-1">
                  {dashboardStats.performance.uptime}
                </div>
                <div className="text-orange-200 text-sm">Tiempo activo</div>
              </div>
            </div>

            {/* Charts Section */}
            <div className="grid lg:grid-cols-2 gap-8 mb-8">
              <div className="bg-gray-800 p-6 rounded-2xl border border-gray-700">
                <h4 className="text-white font-bold mb-4">Ventas por Categoría</h4>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-300">Electrónicos</span>
                    <div className="flex items-center space-x-2">
                      <div className="w-20 h-2 bg-gray-700 rounded-full">
                        <div className="w-16 h-2 bg-blue-500 rounded-full"></div>
                      </div>
                      <span className="text-white text-sm">80%</span>
                    </div>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-300">Ropa</span>
                    <div className="flex items-center space-x-2">
                      <div className="w-20 h-2 bg-gray-700 rounded-full">
                        <div className="w-12 h-2 bg-green-500 rounded-full"></div>
                      </div>
                      <span className="text-white text-sm">60%</span>
                    </div>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-300">Hogar</span>
                    <div className="flex items-center space-x-2">
                      <div className="w-20 h-2 bg-gray-700 rounded-full">
                        <div className="w-8 h-2 bg-purple-500 rounded-full"></div>
                      </div>
                      <span className="text-white text-sm">40%</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gray-800 p-6 rounded-2xl border border-gray-700">
                <h4 className="text-white font-bold mb-4">Stock Crítico</h4>
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                    <div>
                      <span className="text-white font-medium">iPhone 14 Pro</span>
                      <div className="text-red-400 text-sm">Stock: 2 unidades</div>
                    </div>
                    <span className="text-red-400 text-sm font-bold">¡CRÍTICO!</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
                    <div>
                      <span className="text-white font-medium">MacBook Air M2</span>
                      <div className="text-yellow-400 text-sm">Stock: 8 unidades</div>
                    </div>
                    <span className="text-yellow-400 text-sm font-bold">BAJO</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
                    <div>
                      <span className="text-white font-medium">iPad Pro</span>
                      <div className="text-green-400 text-sm">Stock: 45 unidades</div>
                    </div>
                    <span className="text-green-400 text-sm font-bold">OK</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <button className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-3 px-6 rounded-xl font-semibold transition-colors">
                Ver Demo Interactivo
              </button>
              <button className="flex-1 border border-gray-600 text-gray-300 hover:text-white hover:border-gray-500 py-3 px-6 rounded-xl font-semibold transition-colors">
                Descargar Brochure
              </button>
            </div>
          </div>

          {/* Floating Elements */}
          <div className="absolute -top-4 -right-4 bg-gradient-to-r from-green-500 to-green-600 text-white px-4 py-2 rounded-xl text-sm font-bold animate-bounce">
            ¡En Tiempo Real!
          </div>
        </div>

        <div className="text-center mt-16">
          <p className="text-gray-400 text-lg mb-6">
            "El dashboard de SAIP nos permite tomar decisiones en segundos, no en días"
          </p>
          <div className="flex justify-center items-center space-x-4">
            <img 
              src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=50&h=50&fit=crop&crop=face" 
              alt="CEO" 
              className="w-12 h-12 rounded-full"
            />
            <div className="text-left">
              <div className="text-white font-semibold">Roberto Silva</div>
              <div className="text-gray-400 text-sm">CEO, TechnoMart</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Dashboard;